import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
  distinctUntilChanged,
  filter,
  map,
  skip,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { UserSession } from '@shared/models';
import {
  CryptoService,
  OfflineSyncService,
  RoutingService,
  ServiceWorkerUpdateService,
  SubprojectService,
  ZendeskService,
} from '@shared/services';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';

const ERROR_ROUTE = '/error';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  constructor(
    private swUpdateService: ServiceWorkerUpdateService,
    private router: Router,
    private routingService: RoutingService,
    private userDetailsFacade: UserDetailsFacade,
    private cryptoService: CryptoService,
    private subprojectService: SubprojectService,
    private offlineSyncService: OfflineSyncService,
    private zendeskService: ZendeskService,
    private appFacade: AppFacade,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.swUpdateService.isEnabled) {
      this.swUpdateService.checkForUpdates();
    }

    // listen to app online state changes and sync the data to the server when going back online
    this.offlineSyncService.setup();

    // fill in the clientId if it's missing
    // add the right subprojectId if it's missing or invalid with regards to encryption
    this.router.events
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.userDetailsFacade.query.userDetails$),
        filter(
          (value): value is [NavigationEnd, UserSession] =>
            value[0] instanceof NavigationEnd
        ),
        map(([route, userDetails]) => {
          // The error page carries neither a clientId nor a subprojectId
          // segment, so the fix-up below would "correct" it into a route the
          // user has no rights for and bounce them straight back here.
          if (route.urlAfterRedirects.startsWith(ERROR_ROUTE)) {
            return {
              redirectNeeded: false,
              clientId: null,
              subprojectIdFromRoute: null,
              decryptedSubprojectIdFromRoute: 0,
              userDetails,
            };
          }

          let redirectNeeded = false;

          let clientId = this.routingService.routePrefix;
          let subprojectIdFromRoute =
            this.routingService.subprojectIdFromCurrentRoute;
          if (
            String.isNullOrWhiteSpace(clientId) ||
            clientId === 'null' ||
            (userDetails &&
              !userDetails.clientsList.find((x) => x.id === clientId))
          ) {
            clientId = userDetails.selectedClientId;
            subprojectIdFromRoute = this.cryptoService.encryptId(
              userDetails.selectedSubprojectId
            );
            redirectNeeded = true;
          }

          const decryptedSubprojectIdFromRoute = this.cryptoService.decryptId(
            subprojectIdFromRoute
          );
          if (
            subprojectIdFromRoute === undefined ||
            decryptedSubprojectIdFromRoute === 0
          ) {
            subprojectIdFromRoute = this.cryptoService.encryptId(
              userDetails.selectedSubprojectId
            );
            redirectNeeded = true;
          }

          return {
            redirectNeeded,
            clientId,
            subprojectIdFromRoute,
            decryptedSubprojectIdFromRoute,
            userDetails,
          };
        }),
        tap(
          ({
            redirectNeeded,
            clientId,
            subprojectIdFromRoute,
            decryptedSubprojectIdFromRoute,
            userDetails,
          }) => {
            if (redirectNeeded) {
              // With no subproject selected in the session there is nothing to
              // encrypt, and the redirect would build a two-segment `dprs` URL
              // that resolves to "Page not found". Falling back to the first
              // subproject the user has rights on covers both the not-yet-loaded
              // session and the user entitled to none (no-access page).
              if (String.isNullOrWhiteSpace(subprojectIdFromRoute)) {
                this.routingService
                  .goToFirstAllowedSubproject(userDetails)
                  .pipe(untilDestroyed(this))
                  .subscribe();
                return;
              }

              this.routingService.navigateToRoute(
                clientId,
                'dprs',
                subprojectIdFromRoute
              );
            } else if (
              decryptedSubprojectIdFromRoute &&
              userDetails &&
              decryptedSubprojectIdFromRoute !==
                userDetails.selectedSubprojectId
            ) {
              this.subprojectService
                .selectSubproject(decryptedSubprojectIdFromRoute, userDetails)
                .pipe(untilDestroyed(this))
                .subscribe();
            }
          }
        ),
        map(({ redirectNeeded }) => redirectNeeded),
        // sync the Zendesk widget only on the final, settled route (no pending
        // redirect — this filters out the sign-in/client/subproject redirect
        // hops) and once the route's data has loaded (global loader cleared), so
        // it never flashes during the loading screens or sign-in redirects.
        filter((redirectNeeded) => !redirectNeeded),
        switchMap(() =>
          this.appFacade.query.isLoading$.pipe(
            filter((isLoading) => !isLoading),
            take(1)
          )
        ),
        tap(() => this.zendeskService.syncWidget())
      )
      .subscribe();

    // react to connectivity changes so the widget hides the moment the internet
    // drops (and reappears on reconnect) without waiting for a navigation.
    // skip(1) ignores the initial value — the navigation stream above owns the
    // first show, so this doesn't flash the widget during sign-in/loading.
    this.appFacade.query.isAppOnline$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        skip(1),
        tap(() => this.zendeskService.syncWidget())
      )
      .subscribe();

    // close the support panel whenever a dialog opens so it doesn't overlap the
    // dialog. afterOpened on any MatDialog delegates to the root instance, so
    // this catches dialogs opened anywhere (e.g. from the grid in lazy modules).
    this.dialog.afterOpened
      .pipe(
        untilDestroyed(this),
        tap(() => this.zendeskService.close())
      )
      .subscribe();
  }
}
